import axios from 'axios';
import axiosWithMjrAccessToken from 'utils/axiosWithMjrAccessToken';
import { logError } from 'utils/errorLogger';
import * as EstimateDefinition from 'utils/estimateDefinitions';

/**
 * MakeAuthnetCall
 *
 * @param {*} callObject authnet api call object
 *
 * pass call through next api route to access authnet securely
 */
const MakeAuthnetCall = async (callObject) => {
  try {
    const callResponse = await axios.get(`${process.env.NEXT_PUBLIC_INDEX_URL}/api/authnet`, callObject);
    return callResponse;
  } catch (error) {
    logError(error, 'MakeAuthnetCall - utils/payment.js');
    return error;
  }
};

/**
 * getTaxRateOnShipping
 *
 * @param zipCode - Shipping Address zipcode
 * @param callbackFunction - callback to set tax rates
 * get tax rate from zip2tax from mjr api based on shipping zipcode
 */
export const getTaxRateOnShipping = async (zipCode, callbackFunction) => {
  const getTaxRateUrl = `${process.env.NEXT_PUBLIC_PROXY_MJR_API_URL}/api/Payment/GetTaxRateBasedOnZipCode`;
  const getTaxRateData = { zipCode };

  const getTaxRateResponse = await axiosWithMjrAccessToken
    .post(getTaxRateUrl, getTaxRateData)
    .then((response) => {
      callbackFunction(response.data.taxRate);
    })
    .catch((error) => {
      logError(error, 'getTaxRate - /utils/payment.js');
      return error;
    });
  return getTaxRateResponse;
};

/**
 * getTaxRateOnEstimate
 *
 * @param zipCode - Shipping Address zipcode
 * @param callbackFunction - callback to set tax rates
 * get tax rate from zip2tax from mjr api based on shipping zipcode
 */
export const getTaxRateOnEstimate = async (zipCode, callbackFunction) => {
  const getTaxRateUrl = `${process.env.NEXT_PUBLIC_PROXY_MJR_API_URL}/api/Payment/GetTaxRateOnEstimateBasedOnZipCode`;
  const getTaxRateData = { zipCode };

  const getTaxRateResponse = await axiosWithMjrAccessToken
    .post(getTaxRateUrl, getTaxRateData)
    .then((response) => {
      callbackFunction(response.data.taxRate);
    })
    .catch((error) => {
      logError(error, 'getTaxRate - /utils/payment.js');
      return error;
    });
  return getTaxRateResponse;
};

// payment handling with accept.js
const createPaymentAsync = (data) => new Promise((resolve) => {
  window.Accept.dispatchData(data, resolve);
});

// create error object from accept.js response
const parseErrors = (response) => {
  let i = 0;
  const result = [];

  while (i < response.messages.message.length) {
    result.push(`${response.messages.message[i].text}`);
    i += 1;
  }
  return result.join('\n');
};

// create date array from expiration date input
const SplitDate = (expDate) => {
  const dateSplit = expDate.match(/.{1,2}/g);
  return [dateSplit.shift(), dateSplit.join(' ')];
};

// format date for auth net
const FormatExpDate = (expDate) => {
  const dateArray = SplitDate(expDate);
  return `${`20${dateArray[1]}-${dateArray[0]}`}`;
};

/**
 * createPayment
 *
 * @param data - form card information/shipping address if different
 * @param order - repairs order information/includes shipping option selection
 *
 * create a payment transaction and pass back to MJR api
 * @param type
 */
export const createPayment = async (data, order, type) => {
  const isFree = (order?.amount === 0);
  let dateArray;
  if (!isFree) { dateArray = SplitDate(data?.expirationDate); }
  // create billing address
  let billingAddress = order?.details?.address;
  if (data.billingSame === 'no') {
    billingAddress = {
      firstName: data.firstName,
      lastName: data.lastName,
      addressLine1: data.address1,
      addressLine2: data.address2,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
      country: data.country || process.env.NEXT_PUBLIC_US_ISO_CODE,
    };
  }
  const savedCard = data?.customerPaymentProfileId;
  const CreateOrder = async (dataObj) => {
    const callUrl = `${process.env.NEXT_PUBLIC_PROXY_MJR_API_URL}/api/Order/Create`;
    const createOrderCall = await axiosWithMjrAccessToken
      .post(callUrl, dataObj)
      .then((res) => res) // send payment result to our backend
      .catch((error) => {
        logError(error, 'MakeMjrPayment - /utils/payment.js');
        return error;
      });
    return createOrderCall;
  };
  const UpdateOrder = async (dataObj) => {
    const callUrl = `${process.env.NEXT_PUBLIC_PROXY_MJR_API_URL}/api/Order/Update`;
    const updateOrderCall = await axiosWithMjrAccessToken
      .post(callUrl, dataObj)
      .then((res) => res) // send payment result to our backend
      .catch((error) => {
        logError(error, 'MakeMjrPayment - /utils/payment.js');
        return error;
      });
    return updateOrderCall;
  };
  const repairs = [];

  // loop through items to create line items
  if (type !== 'create-order') {
    order.details.repairItems.forEach((item) => {
      if (EstimateDefinition.VALID_CHECKOUT_VALUES.includes(item.itemAction) && item.statusEnum === EstimateDefinition.ESTIMATE_PROVIDED) {
        repairs.push(item);
      }
    });
  }

  const paymentTypeEnum = isFree ? 'NoPaymentRequired' : savedCard ? 'CustomerProfilePayment' : 'NoncePayment';

  const callData = ((type === 'create-order') ? {
    paymentDTO: {
      paymentDetails: {},
      PaymentTypeEnum: paymentTypeEnum,
      billingAddress,
    },
    shipTo: order.details.repairItems[0].shippingToRepairVendor.shipTo,
    ...order.details,
  } : {
    paymentDTO: {
      paymentDetails: {},
      PaymentTypeEnum: paymentTypeEnum,
      billingAddress,
    },
    RepairItems: repairs,
    DestinationAddress: order?.details?.address,
  });

  let cardError = false;
  let error = '';

  if (isFree) {
    callData.paymentDTO.paymentDetails = { isFree };
  } else if (!savedCard) {
    // create secure info from authnet
    const secureData = {
      authData: {
        clientKey: process.env.NEXT_PUBLIC_AUTHNET_CLIENT_KEY,
        apiLoginID: process.env.NEXT_PUBLIC_AUTHNET_API_LOGIN_ID,
      },
      cardData: {
        fullName: `${data.firstName} ${data.lastName}`,
        cardNumber: data.cardNumber,
        cardCode: data.cvv,
        month: dateArray[0],
        year: dateArray[1],
      },
    };
    // create payment from standard
    await createPaymentAsync(secureData)
      .then((response) => {
        if (response.messages.resultCode === 'Error') {
          cardError = true;
          error = { error: true, errorMessage: parseErrors(response) };
        } else {
          callData.paymentDTO.paymentDetails = response.opaqueData;
        }
      })
      .catch((err) => {
        logError(err, 'createPaymentAsync - /utils/payment.js');
        cardError = true;
        error = { error: true, errorMessage: err };
      });
  } else {
    // create payment from saved card
    callData.paymentDTO.paymentDetails = {
      customerProfileId: data.customerProfileId,
      customerPaymentProfileId: data.customerPaymentProfileId,
      customerCardCode: data.cvv,
    };
  }
  if (cardError) {
    return error;
  }

  return (type === 'create-order') ? CreateOrder(callData) : UpdateOrder(callData);
};

// AUTHNET HELPER FUNCTIONS
export const CreateCustomerProfile = async (formData) => {
  const callObject = {
    params: {
      callData: {
        createCustomerProfileRequest: {
          profile: {
            merchantCustomerId: `${formData.firstName} ${formData.lastName}`,
            description: 'MJR Customer payment profile',
            email: formData.user.email,
            paymentProfiles: {
              customerType: 'individual',
              billTo: formData.address,
              payment: {
                creditCard: {
                  cardNumber: formData.cardNumber,
                  expirationDate: FormatExpDate(formData.expirationDate),
                  cardCode: formData.cvv,
                },
              },
            },
          },
          validationMode: 'liveMode',
        },
      },
    },
  };
  const createUser = await MakeAuthnetCall(callObject);
  return createUser;
};

// gets a customers information. -> SSR
export const GetCustomerProfile = async (authNetCustomerProfileId) => {
  const callObject = {
    params: {
      callData: {
        getCustomerProfileRequest: {
          customerProfileId: authNetCustomerProfileId,
          includeIssuerInfo: 'true',
        },
      },
    },
  };
  if (authNetCustomerProfileId !== null) {
    const responseObject = await MakeAuthnetCall(callObject);
    // handle response
    if (responseObject?.data?.messages?.resultCode === 'Ok') {
      return responseObject?.data?.profile;
    }
    return {
      error: true,
      message: 'There was an error getting the customer information',
      responseObject,
    };
  }
  return {
    message: 'no_profile',
  };
};

// gets a customers information using their email address
export const GetCustomerProfileEmail = async (authNetEmail) => {
  const callObjectCheckIfExists = {
    params: {
      callData: {
        getCustomerProfileRequest: {
          email: authNetEmail,
          includeIssuerInfo: 'true',
        },
      },
    },
  };

  if (authNetEmail !== null) {
    const responseObject = await MakeAuthnetCall(callObjectCheckIfExists);
    // handle response
    if (responseObject?.data?.messages?.resultCode === 'Ok') {
      return responseObject?.data?.profile;
    }
    return {
      error: true,
      message: 'There was an error getting the customer information',
      responseObject,
    };
  }
  return {
    message: 'no_profile',
  };
};

// add payment method. -> SSR
// if no profile exists then create an id and then populate add a card.
export const AddPaymentMethod = async (authNetCustomerProfileId, formData) => {
  let addResponse = {};
  let profileNeedsUpdate = null;

  // Checks if the user has an authNet user by their email rather than CustomerProfileId
  // but for some reason their JEMS2 user doesn't have the CustomerProfileId entered
  // if so, marks the profile as needing an update with the CustomerProfileId from authnet
  if (authNetCustomerProfileId === null) {
    await GetCustomerProfileEmail(formData.user.email).then(async (response) => {
      const createResponse = response;
      if (createResponse.error !== true) {
        authNetCustomerProfileId = createResponse?.customerProfileId;
        profileNeedsUpdate = true;
      }
    });
  }
  // makes payment request
  // takes createResponse object from creating account. If no account was created object will be {}
  // check if user doesn't have a payment profile
  if (authNetCustomerProfileId === null) {
    await CreateCustomerProfile(formData).then(async (response) => {
      const createResponse = response;
      profileNeedsUpdate = true;
      addResponse = createResponse;
    });
  } else {
    const callObject = {
      params: {
        callData: {
          createCustomerPaymentProfileRequest: {
            customerProfileId: authNetCustomerProfileId,
            paymentProfile: {
              billTo: formData.address,
              payment: {
                creditCard: {
                  cardNumber: formData.cardNumber,
                  expirationDate: FormatExpDate(formData.expirationDate),
                  cardCode: formData.cvv,
                },
              },
              defaultPaymentProfile: true,
            },
            validationMode: 'liveMode',
          },
        },
      },
    };
    // make authnet call
    addResponse = await MakeAuthnetCall(callObject);
  }
  if (profileNeedsUpdate) { addResponse.newProfile = true; }
  return addResponse;
};

// remove payment method -> SSR
export const RemovePaymentMethod = async (authNetCustomerProfileId, customerPaymentProfileId) => {
  const callObject = {
    params: {
      callData: {
        deleteCustomerPaymentProfileRequest: {
          customerProfileId: authNetCustomerProfileId,
          customerPaymentProfileId,
        },
      },
    },
  };
  const callResponse = await MakeAuthnetCall(callObject);
  return callResponse?.data?.messages?.resultCode === 'Ok';
};

// gets list of all payment mehtods from profile id -> SSR
export const GetPaymentMethods = async (authNetCustomerProfileId, setPaymentLoading, setPaymentProfile) => {
  setPaymentLoading(true);
  await GetCustomerProfile(authNetCustomerProfileId).then((response) => {
    setPaymentProfile({ ...response });
    setPaymentLoading(false);
  });
};
